import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { useLeadLabels, useProperties, usePropertyLabels } from 'src/services/api';
import { StringFormat, formatString } from 'src/utils/format-string';
import { LeadsIcon, PropertiesIcon, UsersIcon } from 'src/icons';
import Chip from 'src/components/chip/Chip';
import { useConversationsPageContext } from 'src/pages/landlord/conversations/context';

import styles from 'src/pages/landlord/conversations/components/header/ConversationFilterStatus.module.css';

export default function ConversationsFilterStatus() {
    const { data: leadLabels } = useLeadLabels();
    const { data: properties } = useProperties();
    const { data: propertyLabels } = usePropertyLabels();

    const { teamMembers, filter, setFilter } = useConversationsPageContext();

    const filteringByAssignees = !!filter.assigneeIds.length;
    const assigneeFilterLabel = `Assignee is ${filter.assigneeIds
        .map(assigneeId => `"${teamMembers?.find(member => member.id === Number(assigneeId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;

    const filteringByIngestionSources = !!filter.ingestionSources.length;
    const ingestionSourcesFilterLabel = `Ingestion source is ${filter.ingestionSources
        .map(source => `"${source}"`)
        .join(' or ')}`;

    const filteringByLeadStages = !!filter.leadStages.length;
    const leadStagesFilterLabel = `Lead stage is ${filter.leadStages
        .map(stage => `"${formatString(stage, StringFormat.SnakeCase, StringFormat.SentenceCase)}"`)
        .join(' or ')}`;

    const filteringByLeadLabels = !!filter.leadLabelIds.length;
    const leadLabelsFilterLabel = `Lead label is ${filter.leadLabelIds
        .map(labelId => `"${leadLabels?.find(label => label.id === Number(labelId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;

    const filteringByExcludeLeadLabels = !!filter.excludeLeadLabelIds.length;
    const excludeLeadLabelsFilterLabel = `Lead label is not ${filter.excludeLeadLabelIds
        .map(labelId => `"${leadLabels?.find(label => label.id === Number(labelId))?.name || ''}"`)
        .filter(Boolean)
        .join(' and ')}`;
    const filteringByArchivalStatus = filter.leadIsArchived !== undefined;
    const archivalStatusFilterLabel = filteringByArchivalStatus ?
        `Lead is ${filter.leadIsArchived ? 'archived' : 'unarchived'}` :
        '';

    const filteringByProperty = !!filter.propertyIds.length;
    const propertyFilterLabel = `Property is ${filter.propertyIds
        .map(propertyId => `"${properties?.find(property => property.id === Number(propertyId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;

    const filteringByPropertyLabels = !!filter.propertyLabelIds.length;
    const propertyLabelsFilterLabel = `Property label is ${filter.propertyLabelIds
        .map(labelId => `"${propertyLabels?.find(label => label.id === Number(labelId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;

    return (
        <>
            {filteringByAssignees && (
                <Tooltip title={assigneeFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={assigneeFilterLabel}
                            icon={<UsersIcon />}
                            onDelete={() => setFilter({ assigneeIds: [] })}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByIngestionSources && (
                <Tooltip title={ingestionSourcesFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={ingestionSourcesFilterLabel}
                            icon={<DownloadRoundedIcon />}
                            onDelete={() => setFilter({ ingestionSources: [] })}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByLeadStages && (
                <Tooltip title={leadStagesFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={leadStagesFilterLabel}
                            icon={<LeadsIcon />}
                            onDelete={() => setFilter({ leadStages: [] })}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByLeadLabels && (
                <Tooltip title={leadLabelsFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={leadLabelsFilterLabel}
                            icon={<LeadsIcon />}
                            onDelete={() => setFilter({ leadLabelIds: [] })}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByExcludeLeadLabels && (
                <Tooltip title={excludeLeadLabelsFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={excludeLeadLabelsFilterLabel}
                            icon={<LeadsIcon />}
                            onDelete={() => setFilter({ excludeLeadLabelIds: [] })}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByArchivalStatus && (
                <Chip className={styles.animatedFadeIn}
                    variant="outlined"
                    label={archivalStatusFilterLabel}
                    icon={<LeadsIcon />}
                    onDelete={() => setFilter({ leadIsArchived: undefined })}
                />
            )}

            {filteringByProperty && (
                <Tooltip title={propertyFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={propertyFilterLabel}
                            icon={<PropertiesIcon />}
                            onDelete={() => setFilter({ propertyIds: [] })}
                        />
                    </div>
                </Tooltip>
            )}

            {filter.hasNoProperty && (
                <Chip className={styles.animatedFadeIn}
                    variant="outlined"
                    label="Lead has no property"
                    icon={<PropertiesIcon />}
                    onDelete={() => setFilter({ hasNoProperty: false })}
                />
            )}

            {filteringByPropertyLabels && (
                <Tooltip title={propertyLabelsFilterLabel}>
                    <div>
                        <Chip className={styles.animatedFadeIn}
                            variant="outlined"
                            label={propertyLabelsFilterLabel}
                            icon={<PropertiesIcon />}
                            onDelete={() => setFilter({ propertyLabelIds: [] })}
                        />
                    </div>
                </Tooltip>
            )}
        </>
    );
}
